/**
 * Progress — editorial horizontal-bar stepper for the generation wizard.
 *
 * Each step renders as a horizontal bar strip on top (filled per state),
 * a mono "Step NN" label, and the step title stacked below. Matches the
 * website-demo reference's .stepper / .step / .step-bar pattern.
 *
 * Bar fill states:
 *   - pending:   scaleX(0)      — empty line
 *   - active:    scaleX(0.45)   — partially filled with accent color
 *   - completed: scaleX(1)      — fully filled with ink color
 */
import React from 'react';

export interface ProgressStep {
  id: string;
  label: string;
}

export interface ProgressProps {
  steps: ProgressStep[];
  currentStep: number;
  className?: string;
}

export const Progress: React.FC<ProgressProps> = ({
  steps,
  currentStep,
  className = '',
}) => {
  return (
    <div
      className={className}
      role="navigation"
      aria-label="Form progress"
      style={{
        display: 'flex',
        gap: 'var(--space-4)',
        padding: 'var(--space-5)',
        background: 'var(--bg-elevated)',
        border: '1px solid var(--line)',
        borderRadius: 'var(--radius-lg)',
      }}
    >
      {steps.map((step, index) => {
        const isActive = index === currentStep;
        const isCompleted = index < currentStep;
        const fillScale = isCompleted ? 1 : isActive ? 0.45 : 0;
        const fillColor = isActive ? 'var(--accent)' : 'var(--ink)';
        return (
          <div
            key={step.id}
            aria-current={isActive ? 'step' : undefined}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-2)',
              paddingTop: 'var(--space-2)',
              minWidth: 0,
            }}
          >
            <div
              style={{
                height: 3,
                borderRadius: 2,
                background: 'var(--line)',
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: fillColor,
                  transform: `scaleX(${fillScale})`,
                  transformOrigin: 'left',
                  transition: 'transform 0.4s cubic-bezier(0.22, 1, 0.36, 1)',
                }}
              />
            </div>
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: '0.7rem',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: isActive ? 'var(--accent)' : 'var(--ink-muted)',
              }}
            >
              Step {String(index + 1).padStart(2, '0')}
            </span>
            <span
              style={{
                fontSize: 'var(--text-sm)',
                fontWeight: 500,
                color: isActive ? 'var(--ink)' : 'var(--ink-soft)',
                lineHeight: 1.3,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {step.label}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default Progress;
